import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, ArrowLeft, ShoppingCart, AlertTriangle } from "lucide-react";
import { InvestmentProperty, fmt } from "@/hooks/useREInvestingSim";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import {
  ScenarioBrief,
  RiskMeter,
  SimWalletImpact,
  LumiSignalCard,
  type RiskLevel,
} from "@/components/lunimar-kit";

interface Props {
  property: InvestmentProperty;
  availableCash: number;
  onBuy: (dpPct: number, rate: number, years: number) => void;
  onBack: () => void;
  processing: boolean;
}

function TipLabel({ label, tip }: { label: string; tip: string }) {
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {label}
      <Tooltip>
        <TooltipTrigger asChild><Info className="w-3 h-3 cursor-help" /></TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    </span>
  );
}

function calcPI(loan: number, rate: number, years: number) {
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return Math.round(loan / n);
  return Math.round(loan * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
}

export default function DealAnalyzer({ property, availableCash, onBuy, onBack, processing }: Props) {
  const realm = getRealm("re_investing");
  const [dpPct, setDpPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);
  const [maintenancePct, setMaintenancePct] = useState(5);

  const price = property.purchase_price;
  const downPayment = Math.round(price * dpPct / 100);
  const closingCosts = Math.round(price * 0.03);
  const totalCash = downPayment + closingCosts;
  const loanAmount = price - downPayment;
  const monthlyMortgage = calcPI(loanAmount, rate / 100, years);
  const taxMonthly = Math.round(price * 0.015 / 12);
  const insMonthly = Math.round(price * 0.007 / 12);
  const maintenanceReserve = Math.round(property.estimated_rent * maintenancePct / 100);
  const vacancyReserve = Math.round(property.estimated_rent * 0.05);
  const hoa = property.hoa_monthly || 0;
  const totalExpenses = monthlyMortgage + taxMonthly + insMonthly + maintenanceReserve + vacancyReserve + hoa;
  const monthlyCashFlow = property.estimated_rent - totalExpenses;
  const annualCashFlow = monthlyCashFlow * 12;
  const cashOnCash = totalCash > 0 ? (annualCashFlow / totalCash * 100) : 0;
  const cashAfter = availableCash - totalCash;
  const canAfford = cashAfter >= 0;

  // Risk meter (0-100)
  const riskScore = useMemo(() => {
    let score = 30;
    if (dpPct < 10) score += 20;
    else if (dpPct < 15) score += 10;
    if (monthlyCashFlow < 0) score += 25;
    else if (monthlyCashFlow < 100) score += 10;
    if (cashAfter < 5000) score += 15;
    if (property.risk_level === "high") score += 10;
    if (property.condition_score === "poor") score += 10;
    return Math.min(100, score);
  }, [dpPct, monthlyCashFlow, cashAfter, property]);

  const riskLevel: RiskLevel =
    riskScore > 75 ? "Severe" : riskScore > 60 ? "High" : riskScore > 40 ? "Medium" : "Low";

  const warnings: string[] = [];
  if (cashAfter < 5000) warnings.push("Very low reserves after purchase. Unexpected costs could put you at risk.");
  if (monthlyCashFlow < 0) warnings.push("This deal has negative monthly cash flow. You will lose money each month.");
  if (dpPct < 5) warnings.push("Very low down payment increases your loan and monthly payment significantly.");

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="mt-1">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <ScenarioBrief
            realm={realm}
            eyebrow={`Deal Analysis Lab · ${property.city}`}
            title={property.property_name}
            intent="Adjust the levers below. Watch how down payment, term, and reserves change the deal's cash flow, risk, and your remaining cash after closing."
          >
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Price", value: fmt(price) },
                { label: "Est. rent", value: `${fmt(property.estimated_rent)}/mo` },
                { label: "Type", value: property.property_type },
                { label: "Condition", value: property.condition_score, capitalize: true },
              ].map(s => (
                <div
                  key={s.label}
                  className="rounded-lg border border-white/10 bg-white/5 p-2 text-center"
                >
                  <p className="text-[10px] font-mono uppercase tracking-wider text-white/55">{s.label}</p>
                  <p className={`mt-0.5 font-heading text-sm font-bold text-white ${s.capitalize ? "capitalize" : ""}`}>{s.value}</p>
                </div>
              ))}
            </div>
          </ScenarioBrief>
        </div>
      </div>

      {/* Sliders */}
      <div
        className="space-y-5 rounded-2xl border p-4 backdrop-blur-md sm:p-5"
        style={{
          borderColor: "hsl(0 0% 100% / 0.10)",
          background: "hsl(228 45% 12% / 0.55)",
        }}
      >
        <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: `hsl(${realm.primaryAccent})` }}>
          Financing levers
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><TipLabel label="Down Payment %" tip="A larger down payment lowers the loan but uses more capital." /><span className="font-mono text-xs font-semibold text-white">{dpPct}% · {fmt(downPayment)}</span></div>
          <Slider value={[dpPct]} onValueChange={(v) => setDpPct(v[0])} min={3} max={50} step={1} />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><TipLabel label="Interest Rate" tip="Even small rate changes significantly impact monthly payments." /><span className="font-mono text-xs font-semibold text-white">{rate.toFixed(1)}%</span></div>
          <Slider value={[rate * 10]} onValueChange={(v) => setRate(v[0] / 10)} min={40} max={90} step={1} />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><TipLabel label="Loan Term" tip="Shorter loans have higher payments but less total interest." /><span className="font-mono text-xs font-semibold text-white">{years} years</span></div>
          <div className="flex gap-2">
            {[15, 30].map(y => (
              <button
                key={y}
                onClick={() => setYears(y)}
                className="flex-1 rounded-lg border py-2 font-mono text-xs uppercase tracking-wider transition-all"
                style={{
                  borderColor: years === y ? `hsl(${realm.primaryAccent} / 0.6)` : "hsl(0 0% 100% / 0.15)",
                  background: years === y ? `hsl(${realm.primaryAccent} / 0.12)` : "transparent",
                  color: years === y ? `hsl(${realm.primaryAccent})` : "hsl(0 0% 100% / 0.6)",
                }}
              >
                {y}-Year
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between"><TipLabel label="Maintenance Reserve %" tip="Budget for repairs. Forgetting them makes deals look better than they are." /><span className="font-mono text-xs font-semibold text-white">{maintenancePct}% · {fmt(maintenanceReserve)}/mo</span></div>
          <Slider value={[maintenancePct]} onValueChange={(v) => setMaintenancePct(v[0])} min={0} max={15} step={1} />
        </div>
      </div>

      {/* Results */}
      <div
        className="space-y-4 rounded-2xl border p-4 backdrop-blur-md sm:p-5"
        style={{
          borderColor: "hsl(0 0% 100% / 0.10)",
          background: "hsl(228 45% 12% / 0.55)",
        }}
      >
        <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: `hsl(${realm.primaryAccent})` }}>
          Financial breakdown
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
          {[
            ["Down Payment", fmt(downPayment)],
            ["Closing Costs (3%)", fmt(closingCosts)],
            ["Total Cash Needed", fmt(totalCash)],
            ["Loan Amount", fmt(loanAmount)],
            ["Monthly Mortgage", fmt(monthlyMortgage)],
            ["Taxes + Insurance", fmt(taxMonthly + insMonthly) + "/mo"],
            ["Maintenance Reserve", fmt(maintenanceReserve) + "/mo"],
            ["Vacancy Reserve", fmt(vacancyReserve) + "/mo"],
            ...(hoa > 0 ? [["HOA", fmt(hoa) + "/mo"]] : []),
            ["Total Monthly Expenses", fmt(totalExpenses)],
          ].map(([label, value]) => (
            <div key={label as string} className="flex justify-between border-b border-white/10 py-1">
              <span className="text-white/60">{label}</span>
              <span className="font-mono font-semibold text-white">{value}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2 pt-2">
          {[
            { label: "Monthly cash flow", value: fmt(monthlyCashFlow), hsl: monthlyCashFlow >= 0 ? "152 70% 55%" : "0 78% 60%" },
            { label: "Cash-on-cash", value: `${cashOnCash.toFixed(1)}%`, hsl: cashOnCash >= 5 ? "152 70% 55%" : realm.primaryAccent },
            { label: "Cash after close", value: fmt(cashAfter), hsl: cashAfter < 5000 ? "0 78% 60%" : realm.primaryAccent },
          ].map(s => (
            <div
              key={s.label}
              className="rounded-lg border p-3 text-center"
              style={{
                borderColor: `hsl(${s.hsl} / 0.35)`,
                background: `hsl(${s.hsl} / 0.10)`,
              }}
            >
              <p className="text-[10px] font-mono uppercase tracking-wider text-white/60">{s.label}</p>
              <p className="mt-0.5 font-heading text-sm font-bold tabular-nums" style={{ color: `hsl(${s.hsl})` }}>{s.value}</p>
            </div>
          ))}
        </div>

        <RiskMeter
          level={riskLevel}
          label="Deal risk profile"
          hint="Composite of down payment, cash flow, reserves, and property risk."
        />

        <SimWalletImpact
          realm={realm}
          delta={-totalCash}
          balanceAfter={cashAfter}
          label="Cash leaving the account at close"
          persisted={false}
        />
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="space-y-2">
          {warnings.map((w, i) => (
            <LumiSignalCard key={i} realm={realm} title="Warning" tag="Risk">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "hsl(0 78% 60%)" }} />
                <span>{w}</span>
              </div>
            </LumiSignalCard>
          ))}
        </div>
      )}

      <Button
        onClick={() => onBuy(dpPct / 100, rate / 100, years)}
        disabled={!canAfford || processing}
        size="lg"
        className="w-full gap-2"
      >
        <ShoppingCart className="w-4 h-4" />
        {processing ? "Processing..." : canAfford ? "Buy Property" : "Insufficient Funds"}
      </Button>
    </div>
  );
}
